/**
 * src/components/spatial-bindings/OrganizationSpatialBindings.tsx
 * The spatial objects one organization holds, shown inside its row.
 *
 * How the list is assembled, and why it cannot be simpler: the registry
 * contract is a key/value store with no index, so nothing on chain can
 * answer "which objects belong to this organization". GeoLink can
 * enumerate objects, so it supplies the candidate ids — but it does not
 * decide which of them belong here. The chain does.
 *
 * That order matters, and getting it backwards was a real bug. This used
 * to filter candidates by `metadata.organization_id` and only then read
 * the chain for the survivors, which meant a binding whose mirror write
 * never landed was invisible: Burj Khalifa sat bound and approved on
 * chain while its organization's list said "none". Worse, the
 * reconciliation state that exists to report exactly that divergence
 * could never fire, because the row was dropped before anything compared
 * the two. A failure that hides the evidence of itself is the one worth
 * designing against.
 *
 * So membership is now a union: the chain names this organization, *or*
 * GeoLink does. The chain half is the authoritative answer. The GeoLink
 * half is kept because an object GeoLink attributes here with no chain
 * binding behind it is precisely a row that needs binding, and listing it
 * is how someone finds it.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { Organization } from '../../v2/services/organizationService';
import {
  buildApproveBinding,
  buildRemoveBinding,
  buildSuspendBinding,
  getSpatialObjects,
  mirrorObjectOrganization,
  shortObjectId,
  type MirrorResult,
  type SpatialObjectRecord,
  type SpatialSigner,
} from '../../v2/services/spatialRegistryService';
import {
  bindingStatusLabel,
  objectStatusLabel,
  reconciliationState,
  relationshipLabel,
  RECONCILIATION_MESSAGE,
} from '../../v2/services/spatialBindingModel';
import type { SpatialObjectAdminRow } from '../../services/spatial/geoLinkSpatialObjectsAdminClient';
import {
  belongsToOrganization,
  CHAIN_VERIFY_LIMIT,
  listCandidateObjects,
  orderCandidatesForVerification,
  readChainOrgId,
  readMirrorOrgId,
} from './objectCandidates';
import {
  Banner, BindingStatusPill, button, hint, mono, ObjectStatusPill, Pill, sectionTitle,
} from './bindingUi';
import { TX_PHASE_LABEL, useSpatialRegistryConfig, useSpatialWrite } from './useSpatialWrite';
import { BindOrganizationDialog, type BindDialogObject } from './BindOrganizationDialog';

export interface OrganizationSpatialBindingsProps {
  org: Organization;
  /** Whether this wallet may attempt writes. The contract decides for real. */
  canWrite: boolean;
  signer: SpatialSigner | null;
  walletAddress: string | null;
  networkPassphrase?: string;
}

interface BoundObject {
  row: SpatialObjectAdminRow | null;
  objectId: string;
  name: string | null;
  record: SpatialObjectRecord | null;
  /** The organization GeoLink attributes this object to, if any. */
  mirrorOrgId: string | null;
}

const MIRROR_TONE: Record<MirrorResult['outcome'], 'ok' | 'warn' | 'error' | 'info'> = {
  written: 'ok',
  unchanged: 'info',
  skipped: 'warn',
  failed: 'error',
};

/** Used only where `mirrorObjectOrganization` returns no reason of its own. */
const MIRROR_DEFAULT_MESSAGE: Record<MirrorResult['outcome'], string> = {
  written: 'GeoLink now records this organization for the object.',
  unchanged: 'GeoLink already recorded this organization for the object.',
  skipped: 'GeoLink was not updated.',
  failed: 'GeoLink could not be updated.',
};

export const OrganizationSpatialBindings: React.FC<OrganizationSpatialBindingsProps> = ({
  org, canWrite, signer, walletAddress, networkPassphrase,
}) => {
  const { configured } = useSpatialRegistryConfig();
  const write = useSpatialWrite(signer, networkPassphrase);

  const [items, setItems] = useState<BoundObject[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [preselected, setPreselected] = useState<BindDialogObject | null>(null);
  /** True when the candidate set outgrew CHAIN_VERIFY_LIMIT, so the list may be short. */
  const [truncated, setTruncated] = useState(false);

  const orgId = org.id.toLowerCase();

  const load = useCallback(async () => {
    if (!configured) return;
    setLoading(true);
    setError(null);
    try {
      const { rows } = await listCandidateObjects(org.alpha2 ?? null);

      // Membership is matched locally rather than through GeoLink's
      // `organization_id` query parameter: that parameter matches the
      // promoted column, which stays null on deployments that have not
      // promoted the metadata key yet.
      const { toVerify, truncated: cut } = orderCandidatesForVerification(
        rows, readMirrorOrgId, orgId,
      );
      setTruncated(cut);

      const records = await getSpatialObjects(toVerify.map((r) => r.id));

      // No country filter is applied to the result. An organization id
      // belongs to exactly one country on chain, so matching it is
      // already country-correct, and re-filtering on GeoLink's
      // frequently-null country column would only drop rows.
      const mine: BoundObject[] = [];
      for (const row of toVerify) {
        const key = row.id.replace(/-/g, '').toLowerCase();
        const record = records.get(key) ?? null;
        const mirrorOrgId = readMirrorOrgId(row);
        if (!belongsToOrganization(readChainOrgId(record), mirrorOrgId, orgId)) continue;
        mine.push({ row, objectId: row.id, name: row.name ?? null, record, mirrorOrgId });
      }

      setItems(mine);
    } catch (e) {
      setError(
        e instanceof Error
          ? `Could not list this organization's objects: ${e.message}`
          : 'Could not list this organization\'s objects',
      );
    } finally {
      setLoading(false);
    }
  }, [configured, org.alpha2, orgId]);

  useEffect(() => { void load(); }, [load]);

  const approve = useCallback(async (item: BoundObject) => {
    if (!walletAddress) return;
    await write.run({
      build: () => buildApproveBinding(item.objectId, walletAddress),
      successMessage: `Approved binding for ${item.name || shortObjectId(item.objectId)}`,
      mirror: { objectId: item.objectId, organizationId: org.id },
      refresh: load,
    });
  }, [walletAddress, write, org.id, load]);

  const suspend = useCallback(async (item: BoundObject) => {
    if (!walletAddress) return;
    await write.run({
      build: () => buildSuspendBinding(item.objectId, walletAddress),
      successMessage: `Suspended binding for ${item.name || shortObjectId(item.objectId)}`,
      refresh: load,
    });
  }, [walletAddress, write, load]);

  const remove = useCallback(async (item: BoundObject) => {
    if (!walletAddress) return;
    await write.run({
      build: () => buildRemoveBinding(item.objectId, walletAddress),
      successMessage: `Removed binding for ${item.name || shortObjectId(item.objectId)}`,
      // Clear the mirror too: leaving it behind would keep the object
      // listed under an organization that no longer holds it.
      mirror: { objectId: item.objectId, organizationId: null },
      refresh: load,
    });
  }, [walletAddress, write, load]);

  /**
   * Write the chain's answer into GeoLink for one object.
   *
   * No transaction and no signer: the binding already exists on chain, and
   * this only catches the mirror up to it. That is why it is offered even
   * where a write action would not be.
   */
  const repairMirror = useCallback(async (item: BoundObject): Promise<MirrorResult> => {
    const result = await mirrorObjectOrganization(item.objectId, org.id);
    if (result.outcome === 'written') await load();
    return result;
  }, [org.id, load]);

  const counts = useMemo(() => {
    let onChain = 0;
    let pending = 0;
    let chainOnly = 0;
    for (const i of items) {
      if (i.record?.binding && i.record.binding.statusName !== 'removed') onChain += 1;
      if (i.record?.binding?.statusName === 'proposed') pending += 1;
      if (reconciliationState(i.record, i.mirrorOrgId) === 'chain_only') chainOnly += 1;
    }
    return { onChain, pending, chainOnly, total: items.length };
  }, [items]);

  if (configured === null) return null;

  if (!configured) {
    return (
      <div style={{ borderTop: '1px solid #eee', paddingTop: '10px', marginTop: '10px' }}>
        <div style={sectionTitle}>Spatial objects</div>
        <div style={hint}>
          The spatial registry contract is not configured on this server, so object
          bindings cannot be read or written. Set <code>GEO_SPATIAL_REGISTRY_ID</code>{' '}
          and restart the backend.
        </div>
      </div>
    );
  }

  return (
    <div style={{ borderTop: '1px solid #eee', paddingTop: '10px', marginTop: '10px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
        <div style={{ ...sectionTitle, marginBottom: 0, flex: 1 }}>
          Spatial objects
          {!loading && (
            <span style={{ ...hint, fontWeight: 400, marginLeft: '6px' }}>
              {/*
                "none" used to cover two very different situations — an
                organization that genuinely holds nothing, and one whose
                objects the list failed to find. Say which.
              */}
              {counts.total === 0
                ? (truncated ? 'none found in the objects checked' : 'none held')
                : `${counts.onChain} on chain of ${counts.total}`}
              {counts.pending > 0 ? ` · ${counts.pending} awaiting approval` : ''}
              {counts.chainOnly > 0 ? ` · ${counts.chainOnly} not in GeoLink` : ''}
            </span>
          )}
        </div>
        {canWrite && (
          <button
            onClick={() => { setPreselected(null); setDialogOpen(true); }}
            disabled={write.busy}
            style={button(!write.busy)}
          >
            Bind spatial object
          </button>
        )}
      </div>

      {error && <Banner tone="warn">{error}</Banner>}
      {write.state.error && <Banner tone="error">{write.state.error}</Banner>}
      {write.state.warning && <Banner tone="warn">{write.state.warning}</Banner>}
      {write.state.notice && <Banner tone="ok">{write.state.notice}</Banner>}
      {write.busy && <Banner tone="info">{TX_PHASE_LABEL[write.state.phase]}</Banner>}

      {loading && <div style={hint}>Loading objects…</div>}

      {!loading && truncated && (
        <Banner tone="warn">
          Only the first {CHAIN_VERIFY_LIMIT} of GeoLink&apos;s objects were checked
          against the chain, so an object bound to this organization may be
          missing from this list. Give the objects a country in GeoLink to
          narrow the search.
        </Banner>
      )}

      {!loading && !items.length && !error && (
        <div style={hint}>
          {truncated
            ? 'None of the objects checked belong to this organization.'
            : `No spatial objects are attributed to this organization${
              org.alpha2 ? ` in ${org.alpha2}` : ''} yet.`}
          {canWrite && ' Use "Bind spatial object" to attach one.'}
        </div>
      )}

      {items.map((item) => (
        <BoundObjectRow
          key={item.objectId}
          item={item}
          canWrite={canWrite}
          busy={write.busy}
          onApprove={() => approve(item)}
          onSuspend={() => suspend(item)}
          onRemove={() => remove(item)}
          onRepairMirror={() => repairMirror(item)}
          onManage={() => {
            setPreselected({
              id: item.objectId,
              name: item.name,
              objectType: item.row?.objectType ?? null,
              countryIso2: item.row?.countryCode ?? item.record?.alpha2 ?? null,
              parentObjectId: item.row?.parentObjectId ?? null,
              organizationId: item.mirrorOrgId,
            });
            setDialogOpen(true);
          }}
        />
      ))}

      {dialogOpen && (
        <BindOrganizationDialog
          mode={preselected ? 'from-object' : 'from-org'}
          organization={org}
          object={preselected}
          countryCode={org.numericCode}
          signer={signer}
          walletAddress={walletAddress}
          networkPassphrase={networkPassphrase}
          onClose={() => { setDialogOpen(false); setPreselected(null); }}
          onCompleted={load}
        />
      )}
    </div>
  );
};

// ── Row ──────────────────────────────────────────────────────────────────────

const BoundObjectRow: React.FC<{
  item: BoundObject;
  canWrite: boolean;
  busy: boolean;
  onApprove: () => void;
  onSuspend: () => void;
  onRemove: () => void;
  onManage: () => void;
  onRepairMirror: () => Promise<MirrorResult>;
}> = ({ item, canWrite, busy, onApprove, onSuspend, onRemove, onManage, onRepairMirror }) => {
  const { record } = item;
  const binding = record?.binding ?? null;
  const state = reconciliationState(record, item.mirrorOrgId);
  const inherited = !!record?.inherited;

  const [repairing, setRepairing] = useState(false);
  const [repairResult, setRepairResult] = useState<MirrorResult | null>(null);

  const repair = useCallback(async () => {
    setRepairing(true);
    setRepairResult(null);
    try {
      setRepairResult(await onRepairMirror());
    } finally {
      setRepairing(false);
    }
  }, [onRepairMirror]);

  const status = binding?.statusName ?? 'none';
  const canApprove = canWrite && (status === 'proposed' || status === 'suspended');
  const canSuspend = canWrite && status === 'approved';
  const canRemove = canWrite && !!binding && status !== 'removed';

  return (
    <div style={{
      border: '1px solid #eee', borderRadius: '4px', padding: '8px 10px', marginBottom: '6px',
      backgroundColor: '#fff',
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: '13px', fontWeight: 600 }}>{item.name || '(unnamed)'}</div>
          <div style={{ ...hint, ...mono }}>
            {item.row?.objectType ? `${item.row.objectType} · ` : ''}
            {item.row?.countryCode || record?.alpha2 || '—'} · {shortObjectId(item.objectId)}…
          </div>
          {record?.parentUuid && (
            <div style={hint}>
              Child of {record.parentUuid.slice(0, 8)}…
              {inherited && ' — organization inherited from it'}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {record
            ? <ObjectStatusPill status={record.statusName} label={objectStatusLabel(record.statusName)} />
            : <Pill tone="neutral">Not on chain</Pill>}
          {binding && (
            <BindingStatusPill status={binding.statusName} label={bindingStatusLabel(binding.statusName)} />
          )}
          {binding && <Pill tone="info">{relationshipLabel(binding.relationship)}</Pill>}
          {inherited && <Pill tone="neutral">Inherited</Pill>}
          {record?.pendingTransfer && <Pill tone="warn">Transfer pending</Pill>}
          {state === 'chain_only' && <Pill tone="warn">Not in GeoLink</Pill>}
        </div>
      </div>

      {binding?.assignedBy && (
        <div style={{ ...hint, marginTop: '4px' }}>
          Approved by {binding.assignedBy.slice(0, 6)}…{binding.assignedBy.slice(-4)}
          {binding.assignedAt ? ` at ledger time ${binding.assignedAt}` : ''}
        </div>
      )}
      {RECONCILIATION_MESSAGE[state] && (
        <div style={{ marginTop: '6px' }}>
          <Banner tone={state === 'chain_only' ? 'info' : 'warn'}>
            {RECONCILIATION_MESSAGE[state]}
          </Banner>
        </div>
      )}

      {state === 'chain_only' && (
        <div style={{ marginTop: '6px' }}>
          <button onClick={repair} disabled={repairing || busy} style={button(!repairing && !busy)}>
            {repairing ? 'Repairing…' : 'Repair mirror'}
          </button>
          {repairResult && (
            <div style={{ marginTop: '6px' }}>
              {/*
                Every outcome is reported, including the quiet ones.
                "Skipped" in particular is not a failure and not a success:
                it means the deployment refused the write, which is
                something to act on rather than retry.
              */}
              <Banner tone={MIRROR_TONE[repairResult.outcome]}>
                {repairResult.reason || MIRROR_DEFAULT_MESSAGE[repairResult.outcome]}
              </Banner>
            </div>
          )}
        </div>
      )}

      {canWrite && (
        <div style={{ display: 'flex', gap: '6px', marginTop: '8px', flexWrap: 'wrap' }}>
          {canApprove && (
            <button onClick={onApprove} disabled={busy} style={button(!busy)}>
              {status === 'suspended' ? 'Reinstate' : 'Approve'}
            </button>
          )}
          {canSuspend && (
            <button onClick={onSuspend} disabled={busy} style={button(!busy, 'warn')}>
              Suspend
            </button>
          )}
          {canRemove && (
            <button onClick={onRemove} disabled={busy} style={button(!busy, 'danger')}>
              Remove
            </button>
          )}
          <button onClick={onManage} disabled={busy} style={button(!busy, 'plain')}>
            {record ? 'Manage' : 'Bind on chain'}
          </button>
        </div>
      )}
    </div>
  );
};
